import React, {Component} from 'react';
import {NavLink, Route, withRouter} from 'react-router-dom';
import {createFragmentContainer, graphql} from 'react-relay';
import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import {FormattedMessage, injectIntl} from 'react-intl';
import moment from 'moment';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../forecast-app/shared/util/util';
import CreateNewMenu from './create_new_menu';
import NotificationMenu from './notifications/notification_menu';
import ProfileMenu from './profile_menu';
import constants, {BUTTON_COLOR, BUTTON_STYLE, ESTIMATION_UNIT, SOCKET_ACTION, SOCKET_EVENT_TYPE, TIERS} from '../../constants';
import Tracking from '../../tracking';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import GlobalSearch from './global_search';
import Timer from '../timer';
import ProjectSectionHeader from './project/project_section_header';
import SettingsSection from '../../containers/settings/settings_section';
import MyProfileSection from '../../containers/my_profile/my_profile_section';
import ShortcutModalContent from '../shortcut_modal_content';
import UpdateViewerMutation from '../../mutations/update_viewer_mutation.modern';
import {withSocketHandling} from '../../socket/withSocketHandling';
import socket_handling from '../../socket/socket_handling';
import {versionManager} from '../../version_manager';
import {EVENT_ID, subscribe, unsubscribe} from '../../containers/event_manager';
import MyWorkSectionHeader from './MyWorkSectionHeader';
import PortfolioSectionHeader from './PortfolioSectionHeader';
import {hasFeatureFlag, usingTimeApproval} from '../../forecast-app/shared/util/FeatureUtil';
import {getBasicUserTypeFromPermissions, hasPermission, isClientUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';

class header extends Component {
	constructor(props) {
		super(props);

		this.handleShortcutApp = this.handleShortcutApp.bind(this);
		this.handleShortcutProject = this.handleShortcutProject.bind(this);
		this.refetchHeader = this.refetchHeader.bind(this);

		// Setting local storage for later use.
		Util.localStorageSetItem('useChargebee', props.viewer.company ? props.viewer.company.useChargebee : false);

		if (
			props.viewer.email &&
			(props.viewer.email.endsWith('forecastit.dk') ||
				props.viewer.email.endsWith('forecast.it') ||
				props.viewer.email.endsWith('forecast.app') ||
				props.viewer.socketFullUpdate)
		) {
			Util.localStorageSetItem('forecast_user', true);
		} else {
			localStorage.removeItem('forecast_user');
		}

		this.state = {
			profileExpanded: false,
			hideTimer: localStorage.getItem('header-hide-timer') ? JSON.parse(localStorage.getItem('header-hide-timer')) : true,
			myWorkLastSection: localStorage.getItem('my-work-section-last-viewed') || '/',
			portfolioLastSection: localStorage.getItem('portfolio-section-last-viewed') || '/scheduling#people',
		};

		// Alternative to the old onUpdate in router.
		this.unlisten = this.props.history.listen((location, action) => {
			if (window.userpilot) {
				const URLcompanyProjectPath = location.pathname.match(/[PX]-\d+/);
				const userId =
					process.env.CIRCLE_BRANCH !== 'production' ? `TEST_${props.viewer.backendId}` : props.viewer.backendId;
				const projectInfo = this.getProjectFromUrl() || this.getProjectGroupFromUrl();
				const isStoryPointProject =
					projectInfo &&
					(projectInfo.estimationUnit === ESTIMATION_UNIT.POINTS ||
						projectInfo.projects?.edges[0]?.node.estimationUnit === ESTIMATION_UNIT.POINTS);
				window.userpilot.identify(userId, {
					currentProjectId: URLcompanyProjectPath ? URLcompanyProjectPath[0] : null,
					isStoryPointProject: isStoryPointProject,
				});
				window.userpilot.reload();
			}
		});
	}

	componentDidMount() {
		const webFrontVersion = versionManager.getClientMajorVersion() + '.' + versionManager.getClientMinorVersion();
		const webFrontVersionTime = moment(versionManager.getClientVersionTime());
		// eslint-disable-next-line no-console
		console.log({webFrontVersion, webFrontVersionTime: webFrontVersionTime.format('DD/MM/YY HH:mm')}); //needed to show this in log rocket
		subscribe(EVENT_ID.MULTIPLE_PROJECTS_ADDED, this.refetchHeader);
		subscribe(EVENT_ID.DEMO_CREATED, this.refetchHeader);
		if (this.props.viewer && this.props.viewer.actualPersonId !== null) {
			document.addEventListener('keydown', this.handleShortcutProject);
			document.addEventListener('keydown', this.handleShortcutApp);
			this.validateProjectUrl();

			// Check the trial end
			const securityCheck = () => {
				if (
					this.props.viewer &&
					this.props.viewer.company &&
					this.props.viewer.company.tier === TIERS.TRIAL &&
					this.props.history.location.pathname !== '/trial-end'
				) {
					const trialEndTime = moment.utc(this.props.viewer.company.trialEndTime);
					const now = moment.utc();
					if (trialEndTime.isBefore(now)) {
						this.props.history.push('/trial-end');
					}
				} else if (
					this.props.viewer &&
					this.props.viewer.company &&
					this.props.viewer.company.accessBlocked &&
					!['/trial-end', '/access-blocked', '/settings/subscription'].includes(this.props.history.location.pathname)
				) {
					this.props.history.push('/access-blocked');
				}

				// Update version automatically every hour
				this.updateWebFrontVersion();
			};
			securityCheck();
			this.intervalID = setInterval(securityCheck, 60 * 60 * 1000); // every hour

			if (
				this.props.viewer.actualPersonId !== null &&
				(Util.getCsrfValue() === null || Util.getCsrfValue() !== this.props.viewer.csrfToken)
			) {
				Util.localStorageSetItem('CSRF-TOKEN', this.props.viewer.csrfToken);
			}

			const socketEvents = [
				{
					type: SOCKET_EVENT_TYPE.PERSON_NOTIFICATION,
					action: SOCKET_ACTION.UPDATE,
					personIds: [this.props.viewer.backendId],
				},
				{
					type: SOCKET_EVENT_TYPE.PROJECT,
					action: SOCKET_ACTION.DELETE,
				},
			];
			if (!hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL)) {
				socketEvents.push({
					type: SOCKET_EVENT_TYPE.PROJECT_PERSON,
					action: SOCKET_ACTION.DELETE,
					personIds: this.props.viewer.backendId,
				});
				socketEvents.push({
					type: SOCKET_EVENT_TYPE.PROJECT_PERSON,
					action: SOCKET_ACTION.CREATE,
					personIds: this.props.viewer.backendId,
				});
			} else {
				socketEvents.push({
					type: SOCKET_EVENT_TYPE.PROJECT,
					action: SOCKET_ACTION.CREATE,
				});
			}
			this.props.setSocketConfig(socketEvents);

			if (this.props.viewer.company.socketEnabled) {
				socket_handling.connect(this.props.viewer.socketFullUpdate, this.props.viewer.backendId);
			}

			const basicUserType = getBasicUserTypeFromPermissions();

			Sentry.configureScope(scope => {
				scope.setUser({
					email: this.props.viewer.email,
					id: atob(this.props.viewer.actualPersonId).replace('Person:', ''),
					company_id: atob(this.props.viewer.company.id).replace('Company:', ''),
					name: this.props.viewer.firstName + ' ' + this.props.viewer.lastName,
					userType: basicUserType,
					company_name: this.props.viewer.company.name,
				});
			});

			try {
				const forecast_user = localStorage.getItem('forecast_user');
				if (window.amplitude) {
					const viewer = this.props.viewer;
					window.amplitude
						.getInstance()
						.setVersionName(`${versionManager.getClientMajorVersion()}.${versionManager.getClientMinorVersion()}`);
					window.amplitude.getInstance().setUserId(viewer.backendId);

					const companyAllocationType = viewer.company.isUsingProjectAllocation
						? 'Project Allocation'
						: viewer.company.isUsingProjectAllocation
						? 'Task Estimates'
						: 'Time Registrations & Remaining Task Estimates';

					const identify = new window.amplitude.Identify()
						.set('email', viewer.email)
						.set('first_name', viewer.firstName)
						.set('last_name', viewer.lastName)
						.set('created', viewer.createdAt)
						.set('tier', viewer.company.tier)
						.set('role', viewer.role && viewer.role.name ? viewer.role.name : 'No role')
						.set('userSeats', viewer.company.userSeats)
						.set('virtualSeats', viewer.company.virtualSeats)
						.set('minimum_seats', viewer.company.minimumSeats)
						.set('first_person', viewer.isFirstPersonInCompany)
						.set('user_type', basicUserType)
						.set('company_id', viewer.company.backendId)
						.set('company_name', viewer.company.name)
						.set('company_allocation_type', companyAllocationType)
						.set('integration_enabled', Util.getIntegrationsEnabled(viewer.company))
						.set('task_modal_open', false)
						.set('use_time_locking', viewer.company.lockedPeriodYear !== null)
						.set('forecast_user', forecast_user === 'true');
					window.amplitude.getInstance().identify(identify);

					if (process.env.CIRCLE_BRANCH !== 'production') {
						// eslint-disable-next-line no-console
						console.log('TRACKING IDENTIFY: ', identify.userPropertiesOperations.$set);
					}
				}

				if (process.env.CIRCLE_BRANCH === 'production' && forecast_user !== 'true') {
					LogRocket.init('forecast/forecast-nod93');
					Sentry.configureScope(scope => {
						scope.addEventProcessor(async event => {
							event.extra = {sessionURL: LogRocket.sessionURL};
							return event;
						});
					});

					LogRocket.identify(atob(this.props.viewer.actualPersonId).replace('Person:', ''), {
						name: this.props.viewer.firstName + ' ' + this.props.viewer.lastName,
						email: this.props.viewer.email,

						// Add your own custom user variables here, ie:
						tier: this.props.viewer.company.tier,
						userType: basicUserType,
						company_name: this.props.viewer.company.name,
						company_id: atob(this.props.viewer.company.id).replace('Company:', ''),
					});
				}

				if (window.userpilot && !this.props.viewer.email.endsWith('forecastit.dk')) {
					const viewer = this.props.viewer;
					const userId = process.env.CIRCLE_BRANCH !== 'production' ? `TEST_${viewer.backendId}` : viewer.backendId;
					const URLcompanyProjectPath = window.location.pathname.match(/[PX]-\d+/);
					const projectInfo = this.getProjectFromUrl() || this.getProjectGroupFromUrl();
					const isStoryPointProject =
						projectInfo &&
						(projectInfo.estimationUnit === ESTIMATION_UNIT.POINTS ||
							projectInfo.projects?.edges[0]?.node.estimationUnit === ESTIMATION_UNIT.POINTS);
					window.userpilot.identify(
						userId, // Used to identify users
						{
							created_at: new Date(viewer.createdAt).getTime() / 1000, // Signup date as a Unix timestamp
							tier: viewer.company.tier,
							seats: viewer.company.userSeats,
							firstPerson: viewer.isFirstPersonInCompany,
							userType: basicUserType,
							companyCreatedAt: viewer.company.createdAt / 1000,
							companyName: viewer.company.name,
							companyId: viewer.company.backendId,
							hasNewNavigation: true,
							hasUtilizationReport: true,
							currentProjectId: URLcompanyProjectPath ? URLcompanyProjectPath[0] : null,
							isStoryPointProject: isStoryPointProject,
						}
					);
				}

				/*if (config.environment === 'master' || config.environment === 'production') {
					const viewer = this.props.viewer;
					datadogRum.setUser({
						id: atob(viewer.actualPersonId).replace('Person:', ''),
						name: viewer.firstName + ' ' + viewer.lastName,
						email: viewer.email,
						userType: basicUserType,
						plan: viewer.company.tier,
						companyName: viewer.company.name,
						companyId: viewer.company.backendId,
					});
				}*/
			} catch (e) {}

			try {
				const viewer = this.props.viewer;

				// Do not track forecast users
				const forecast_user = false; //localStorage.getItem('forecast_user');
				if (forecast_user !== 'true') {
					// Hubspot
					const _hsq = (window._hsq = window._hsq || []);
					_hsq.push([
						'identify',
						{
							email: viewer.email,
							id: viewer.actualPersonId,
							firstname: viewer.firstName,
							lastname: viewer.lastName,
							forecast_user_permission: basicUserType,
						},
					]);
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.log(e.message);
			}

			const language = this.props.viewer.language;
			this.props.setLocale(language);

			/*if (this.props.viewer.company.trialEnded && this.props.history.location.pathname !== '/trial-end') {
			this.props.history.push('/trial-end');
		} else if (this.props.viewer.company.accessBlocked && !['/trial-end', '/access-blocked', '/settings/subscription'].includes(this.props.history.location.pathname)) {
			this.props.history.push('/access-blocked');
		}*/

			//If notification for setting projects to running on their start date hasn't been shown to the viewer yet and viewer is allowed to modify project stage, show it
			//We only check the date and permission level here, checking if there actually are any projects that start today is done within the modal
			if (
				!this.props.viewer.email.endsWith('forecastit.dk') &&
				this.props.viewer.onboardingInitialStep !== 0 &&
				!moment().isSame(moment(this.props.viewer.setToRunningNotificationLastDismissalDate), 'day') &&
				hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE)
			) {
				showModal({
					type: MODAL_TYPE.SET_TO_RUNNING_REMINDER,
				});
			}
		}

		this.updateWebFrontVersion();
	}

	componentDidUpdate(prevProps, prevState, prevContext) {
		if (
			this.props.viewer &&
			this.props.viewer.company &&
			this.props.viewer.company.tier === TIERS.TRIAL &&
			this.props.history.location.pathname !== '/trial-end'
		) {
			const trialEndTime = moment.utc(this.props.viewer.company.trialEndTime);
			const now = moment.utc();
			if (trialEndTime.isBefore(now)) {
				this.props.history.push('/trial-end');
			}
		} else if (
			this.props.viewer &&
			this.props.viewer.company &&
			this.props.viewer.company.accessBlocked &&
			!['/trial-end', '/access-blocked', '/settings/subscription'].includes(this.props.history.location.pathname)
		) {
			this.props.history.push('/access-blocked');
		}

		this.updateWebFrontVersion();
	}

	componentWillUnmount() {
		unsubscribe(EVENT_ID.MULTIPLE_PROJECTS_ADDED, this.refetchHeader);
		unsubscribe(EVENT_ID.DEMO_CREATED, this.refetchHeader);
		document.removeEventListener('keydown', this.handleShortcutProject);
		document.removeEventListener('keydown', this.handleShortcutApp);

		this.unlisten();

		clearInterval(this.intervalID);
		socket_handling.disconnect();
	}

	refetchHeader() {
		if (this.props.retry) {
			this.props.retry();
		}
	}

	updateWebFrontVersion() {
		if (this.lastVersionCheck === undefined || this.lastVersionCheck.isBefore(moment().subtract(10, 'minutes'))) {
			this.lastVersionCheck = moment();
			fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'web_front_version').then(response => {
				response.json().then(webFrontVersion => {
					versionManager.updateWithWebFrontVersionResponse(webFrontVersion);
				});
			});
		}
	}

	validateProjectUrl() {
		const pathname = this.props.history.location.pathname;
		if (pathname.startsWith('/project/P')) {
			// find project
			const project = this.getProjectFromUrl();
			if (!project) {
				this.props.history.replace('/not-found');
			}
		}
		if (pathname.startsWith('/connected/X')) {
			const projectGroup = this.getProjectGroupFromUrl();
			if (!projectGroup) {
				this.props.history.replace('/not-found');
			}
		}
	}

	getProjectFromUrl = () => {
		let project;
		const pathname = this.props.history.location.pathname;
		if (pathname.startsWith('/project/P')) {
			// find project
			const r = /\d+/g;
			const projectId = parseInt(pathname.match(r)[0]);
			project = this.props.viewer.projects.edges.find(edge => edge.node.companyProjectId === projectId);
			if (project) {
				project = project.node;
			}
		}

		return project;
	};

	getProjectGroupFromUrl() {
		let projectGroup;
		const pathname = this.props.history.location.pathname;
		if (pathname.startsWith('/connected/X')) {
			// find project id
			const r = /\d+/g;
			const projectGroupId = parseInt(pathname.match(r)[0]);
			projectGroup = this.props.viewer.headerProjectGroups.edges.find(
				edge => edge.node.companyProjectGroupId === projectGroupId
			);
			if (projectGroup) {
				projectGroup = projectGroup.node;
			}
		}
		return projectGroup;
	}

	switchLanguage(e) {
		// eslint-disable-next-line no-console
		console.log(e);

		let language = null;
		switch (e.keyCode) {
			case 68: //D
				language = 'DANISH';
				break;
			case 69: //E
				language = 'ENGLISH_UK';
				break;
			case 70: //F
				language = 'FRENCH';
				break;
			case 85: //U
				language = 'ENGLISH_US';
				break;
			default:
				break;
		}

		if (language) {
			const onSuccess = () => {
				this.props.setLocale(language);
			};
			Util.CommitMutation(UpdateViewerMutation, {viewer: this.props.viewer, language: language}, onSuccess, true, true);
		}
	}

	handleShortcutApp(e) {
		if (e.shiftKey) {
			if (document.activeElement && document.activeElement.nodeName === 'INPUT') {
				return;
			}
			if (
				document.activeElement &&
				document.activeElement.className &&
				document.activeElement.className.includes('DraftEditor')
			) {
				return;
			}
			if (e.altKey) {
				if (hasFeatureFlag('shortcuts_languages') && e.ctrlKey) {
					/*
					 *	alt/option + control + shift
					 * 	and since it's more for testing purpose I feature flagged it
					 */
					this.switchLanguage(e);
					return;
				}

				switch (e.keyCode) {
					case 87:
						this.props.history.push('/');
						break;
					case 69:
						this.props.history.push('/projects');
						break;
					case 82:
						this.props.history.push('/time');
						break;
					case 84:
						this.props.history.push('/scheduling');
						break;
					case 89:
						this.props.history.push('/insights');
						break;
					case 78:
						//New task shortcut
						showModal({
							type: MODAL_TYPE.NEW_TASK,
							isOpenedByShortcut: true,
							showTaskModal: this.props.showTaskModal,
						});
						break;
					case 83:
						const searchInputId = 'global-search-input';
						if (document.getElementById(searchInputId)) {
							document.getElementById(searchInputId).focus();
						}
						break;
					case 81:
						showModal({
							type: MODAL_TYPE.GENERIC,
							headerText: this.props.intl.formatMessage({id: 'header.shortcuts'}),
							content: <ShortcutModalContent />,
						});
						break;
					default:
						break;
				}
			}
		}
	}

	handleShortcutProject(e) {
		if (e.altKey && e.shiftKey) {
			const project = this.getProjectFromUrl();
			const projecGroup = this.getProjectGroupFromUrl();
			if (!project && !projecGroup) {
				return;
			}
			const navLinkBase = project
				? constants.projectUrl(project.companyProjectId)
				: constants.projectGroupUrl(projecGroup.companyProjectGroupId);
			if (document.activeElement && document.activeElement.nodeName === 'INPUT') {
				return;
			}
			if (
				document.activeElement &&
				document.activeElement.className &&
				document.activeElement.className.includes('DraftEditor')
			) {
				return;
			}
			switch (e.keyCode) {
				case 80:
					if (!Util.AuthorizeViewerAccess('connected-projects')) return;
					this.props.history.push(navLinkBase + '/connected-projects');
					break;
				case 79:
					if (!Util.AuthorizeViewerAccess('project-overview')) return;
					this.props.history.push(navLinkBase + '/overview');
					break;
				case 49:
					this.props.history.push(navLinkBase + '/workflow');
					break;
				case 50:
					if (!Util.AuthorizeViewerAccess('project-phases') || projecGroup) return;
					this.props.history.push(navLinkBase + '/scoping');
					break;
				case 51:
					if (!Util.AuthorizeViewerAccess('project-timeline') || projecGroup) return;
					this.props.history.push(navLinkBase + '/schedule');
					break;
				case 52:
					if (
						this.props.viewer.projectGroup ||
						(project.sprintTimeBox && Util.AuthorizeViewerAccess('project-sprint'))
					) {
						this.props.history.push(navLinkBase + '/sprints');
					}
					break;
				case 53:
					if (!Util.AuthorizeViewerAccess('project-files') || projecGroup) return;
					this.props.history.push(navLinkBase + '/files');
					break;
				case 54:
					if (!Util.AuthorizeViewerAccess('project-budget') || projecGroup) return;
					this.props.history.push(navLinkBase + '/budget');
					break;
				case 55:
					if (!Util.AuthorizeViewerAccess('project-team')) return;
					this.props.history.push(navLinkBase + '/team');
					break;
				case 56:
					if (!Util.AuthorizeViewerAccess('project-client') || projecGroup) return;
					this.props.history.push(navLinkBase + '/client');
					break;
				case 57:
					if (!Util.AuthorizeViewerAccess('project-rates') || projecGroup) return;
					this.props.history.push(navLinkBase + '/rates');
					break;
				case 48:
					if (!Util.AuthorizeViewerAccess('project-settings')) return;
					if (projecGroup) {
						this.props.history.push(navLinkBase + '/group-settings');
					} else {
						this.props.history.push(navLinkBase + '/settings');
					}
					break;
				default:
					break;
			}
		}
	}

	setMyWorkLastSection(section) {
		this.setState({myWorkLastSection: section});
		Util.localStorageSetItem('my-work-section-last-viewed', section);
	}

	setPortfolioLastSection(section) {
		this.setState({portfolioLastSection: section});
		Util.localStorageSetItem('portfolio-section-last-viewed', section);
	}

	mainMenuOnKeyUp(event) {
		event = event || window.event;
		if (event.keyCode === 32) {
			event.preventDefault(); // prevent scrolling
			event.target.click(); // execute regular click event on the link
		}
	}

	onLogoClick(event) {
		event = event || window.event;
		if (event.target) {
			event.target.blur();
		}
	}

	bookDemo() {
		Tracking.trackEvent('Book Demo Button Clicked');
		window.open('https://www.forecast.app/book-a-demo');
	}

	buyNow() {
		Tracking.trackEvent('Header Buy Now Clicked');
		this.props.history.push('/trial-end#licensing');
	}

	toggleTimer() {
		this.setState({hideTimer: !this.state.hideTimer});
		Util.localStorageSetItem('header-hide-timer', !this.state.hideTimer);
	}

	render() {
		const viewer = this.props.viewer;

		if (!viewer.company) return null;

		const {formatMessage} = this.props.intl;
		const isViewerClientUser = isClientUser();
		const isProjectOwner = viewer.projectOwner;
		const timeApprovalAccess =
			usingTimeApproval(this.props.viewer.company) &&
			(hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) || isProjectOwner);

		const pathname = this.props.history.location.pathname;
		const inAdminOrProfileSection =
			this.props.history.location.pathname.startsWith('/settings') ||
			this.props.history.location.pathname.startsWith('/my-profile');
		const inIndividualProjectSection = pathname.startsWith('/project/P');
		const inIndividualProjectGroupSection = pathname.startsWith('/connected/X');
		const project = this.getProjectFromUrl();
		const projectGroup = this.getProjectGroupFromUrl();
		const rootPath = ['/', '/T:taskId(\\d+)']; // Need special handling for root path since Route does not play nice with Task Modal otherwise
		const myWorkPath = ['/timesheets', '/expenses'];

		const portfolioPath = [];
		if (Util.AuthorizeViewerAccess('insights')) {
			portfolioPath.push('/insights', '/saved-report');
		}
		if (Util.AuthorizeViewerAccess('unit4-log')) {
			portfolioPath.push('/unit4log');
		}
		if (Util.AuthorizeViewerAccess('invoicing')) {
			portfolioPath.push('/invoicing', '/invoicingv2');
		}
		if (Util.AuthorizeViewerAccess('sisense') && Util.hasSisense() && hasPermission(PERMISSION_TYPE.SISENSE_READ)) {
			portfolioPath.push('/advanced-analytics');
		}

		const schedulingAccess = Util.AuthorizeViewerAccess('scheduling');
		const myWorkScheduling = schedulingAccess && portfolioPath.length === 0;
		if (myWorkScheduling) {
			myWorkPath.push('/scheduling');
		} else if (schedulingAccess) {
			portfolioPath.push('/scheduling#people', '/scheduling#projects', '/scheduling');
		}

		if (timeApprovalAccess || Util.AuthorizeViewerAccess('team-time')) {
			portfolioPath.push('/time-management');
		}

		const isTrial = this.props.viewer.company.tier === TIERS.TRIAL;

		const hasFinance = Util.hasFinance();

		if (this.props.viewer && this.props.viewer.company && this.props.viewer.company.accessBlocked) {
			return <>{this.props.children}</>;
		}

		return (
			<>
				<header
					className={
						'app-header-v2' +
						(!inIndividualProjectSection && !inIndividualProjectGroupSection && !inAdminOrProfileSection
							? ' include-separator'
							: ' no-separator')
					}
				>
					<div className="content">
						<NavLink
							to="/"
							className="logo"
							role="button"
							onKeyUp={this.mainMenuOnKeyUp.bind(this)}
							onClick={this.onLogoClick.bind(this)}
						>
							<span className="visually-hidden">{this.props.intl.formatMessage({id: 'header.my_work'})}</span>
						</NavLink>
						<ul className="left main-menu">
							<li data-userpilot={'header-my-work'} data-cy={'header-my-work-section-nav'}>
								<NavLink
									to={this.state.myWorkLastSection}
									isActive={(match, location) =>
										location.pathname.match(/^\/(T\d+)?$/) ||
										myWorkPath.includes('/' + location.pathname.split('/')[1])
									}
									activeClassName="active"
									className="section-link"
									role="button"
								>
									<FormattedMessage id="header.my_work" />
								</NavLink>
							</li>
							<li data-userpilot={'header-projects'} data-cy={'header-projects-section-nav'}>
								<NavLink
									to="/projects"
									activeClassName="active"
									className="section-link projects"
									role="button"
									style={
										project && project.projectColor
											? {
													backgroundColor: project.projectColor,
													color: Util.getTextColorV2(project.projectColor),
											  }
											: projectGroup && projectGroup.color
											? {
													backgroundColor: projectGroup.color,
													color: Util.getTextColorV2(projectGroup.color),
											  }
											: {}
									}
									onKeyUp={this.mainMenuOnKeyUp.bind(this)}
								>
									<FormattedMessage id="common.all-projects" />
								</NavLink>
							</li>
							{portfolioPath.length > 0 ? (
								<li data-userpilot={'header-portfolio'} data-cy={'header-portfolio-section-nav'}>
									<NavLink
										to={
											portfolioPath.includes(localStorage.getItem('portfolio-section-last-viewed'))
												? localStorage.getItem('portfolio-section-last-viewed')
												: portfolioPath[0]
										}
										isActive={(match, location) =>
											portfolioPath.find(path => location.pathname.includes(path))
										}
										activeClassName="active"
										className={'section-link'}
										role="button"
										onKeyUp={this.mainMenuOnKeyUp.bind(this)}
									>
										<div>{viewer.company.name || formatMessage({id: 'common.company'})}</div>
									</NavLink>
								</li>
							) : null}
						</ul>

						<div className="right">
							{isTrial ? (
								<Button
									text={formatMessage({id: 'common.book-demo'})}
									buttonStyle={BUTTON_STYLE.FILLED}
									colorTheme={BUTTON_COLOR.PURPLE}
									onClick={this.bookDemo.bind(this)}
									cy={'book-demo-button'}
								/>
							) : null}
							{hasFeatureFlag('no_time_reg') || isViewerClientUser || this.state.hideTimer ? null : (
								<Timer viewer={this.props.viewer} />
							)}

							<GlobalSearch showTaskModal={taskId => Util.showTaskModal(taskId, this.props.history)} />
							<CreateNewMenu
								viewer={this.props.viewer}
								project={project}
								projectGroup={projectGroup}
								showTaskModal={taskId => Util.showTaskModal(taskId, this.props.history)}
							/>
							<NotificationMenu viewer={this.props.viewer} retry={this.props.retry} relay={this.props.relay} />
							<ProfileMenu
								{...this.props}
								hideTimer={this.state.hideTimer}
								toggleTimer={this.toggleTimer.bind(this)}
							/>
						</div>
					</div>
				</header>
				<Route
					exact
					path={rootPath}
					render={() => (
						<MyWorkSectionHeader
							isClientUser={isViewerClientUser}
							myWorkScheduling={myWorkScheduling}
							hasFinance={hasFinance}
							setMyWorkLastSection={this.setMyWorkLastSection.bind(this)}
						/>
					)}
				/>

				<Route
					path={myWorkPath}
					render={() => (
						<MyWorkSectionHeader
							isClientUser={isViewerClientUser}
							myWorkScheduling={myWorkScheduling}
							hasFinance={hasFinance}
							setMyWorkLastSection={this.setMyWorkLastSection.bind(this)}
						/>
					)}
				/>
				<Route
					path={['/project/P-:projectId(\\d+)', '/connected/X-:groupId(\\d+)']}
					render={() => (
						<ProjectSectionHeader
							viewer={viewer}
							project={project}
							projectGroup={projectGroup}
							projects={viewer.projects}
							projectGroups={viewer.headerProjectGroups}
						/>
					)}
				/>
				{!isViewerClientUser && portfolioPath.length > 0 ? (
					<Route
						path={portfolioPath}
						render={() => (
							<PortfolioSectionHeader
								company={viewer.company}
								hasFinance={hasFinance}
								setPortfolioLastSection={this.setPortfolioLastSection.bind(this)}
								portfolioPath={portfolioPath}
								isProjectOwner={isProjectOwner}
							/>
						)}
					/>
				) : null}
				<Route path="/settings" render={() => <SettingsSection viewer={viewer} />} />
				<Route path="/my-profile" render={() => <MyProfileSection />} />
			</>
		);
	}
}

const headerQuery = graphql`
	query header_Query($majorVersion: Int, $minorVersion: Int) {
		viewer {
			actualPersonId
			component(name: "header")
			...header_viewer @arguments(majorVersion: $majorVersion, minorVersion: $minorVersion)
		}
	}
`;

export {headerQuery};

export default withRouter(
	withSocketHandling(
		injectIntl(
			createFragmentContainer(header, {
				viewer: graphql`
					fragment header_viewer on Viewer
					@argumentDefinitions(majorVersion: {type: "Int"}, minorVersion: {type: "Int"}) {
						id
						webFrontVersion(majorVersion: $majorVersion, minorVersion: $minorVersion)
						csrfToken
						actualPersonId
						backendId
						socketFullUpdate
						firstName
						lastName
						email
						language
						isFirstPersonInCompany
						setToRunningNotificationLastDismissalDate
						profilePictureId
						profilePictureDefaultId
						userType
						projectOwner
						role {
							name
						}
						createdAt
						unseenNotifications
						initials
						recentNotifications {
							id
							countId
							publisherPersonId
							subscriberPersonId
							publisherAction
							entityType
							entityId
							params
							createdAt
							read
						}
						notifyInAppOnProjectStatusChange
						notifyInAppOnPersonJoin
						notifyInAppOnAssignedProject
						notifyInAppOnProjectDeadline
						notifyInAppOnAssignedTask
						notifyInAppOnAssignedTaskUpdate
						notifyInAppOnTaskDeadline
						notifyInAppOnMention
						notifyInAppOnTaskDescriptionChange
						notifyInAppOnTaskTitleChange
						notifyInAppOnTaskEstimateChange
						notifyInAppOnTaskStartDateChange
						notifyInAppOnTaskEndDateChange
						notifyInAppOnTaskSprintChange
						notifyInAppOnTaskPhaseChange
						notifyInAppOnTaskStatusColumnChange
						notifyInAppOnTaskProjectChange
						notifyInAppOnTaskSubtaskChange
						notifyInAppOnTaskSubtaskEstimateChange
						notifyInAppOnTaskCommentChange
						notifyInAppOnTaskFileChange
						notifyInAppOnTaskBugChange
						notifyInAppOnTaskBlockedChange
						notifyInAppOnTaskRepeatingChange
						timerTask {
							id
						}
						timerStartDate
						timerEndDate
						sisenseUserId
						company {
							id
							name
							useTimeApproval
							tier
							isChargebeeCustomer
							companySize
							backendId
							userSeats
							virtualSeats
							minimumSeats
							trialEnded
							trialEndTime
							accessBlocked
							hideFiles
							socketEnabled
							isUsingSchedulingPlanMode
							isUsingProjectAllocation
							slackEnabled
							teamsEnabled
							vstsEnabled
							githubEnabled
							gitlabEnabled
							salesforceEnabled
							unit4Enabled
							jiraCloudEnabled
							timelogEnabled
							xeroEnabled
							newXeroEnabled
							quickbooksEnabled
							economicEnabled
							oktaEnabled
							oneloginEnabled
							AADEnabled
							gdriveEnabled
							harvestEnabled
							lockedPeriodYear
							allPersons(first: 48714, includeSystem: true) {
								edges {
									node {
										id
										fullName
										profilePictureId
										profilePictureDefaultId
										initials
									}
								}
							}
						}
						projects(first: 1000000) @connection(key: "Header_Viewer_projects", filters: []) {
							edges {
								node {
									id
									companyProjectId
									status
									name
									projectColor
									isInProjectGroup
									useBaseline
									billable
									budgetType
									projectGroupId
									sprintTimeBox
									estimationUnit ##for userpilot and storypoints performance#
								}
							}
						}
						headerProjectGroups: projectGroups(first: 1000000)
							@connection(key: "Viewer_headerProjectGroups", filters: []) {
							edges {
								node {
									id
									companyProjectGroupId
									name
									color
									projects(first: 1000000) @connection(key: "ProjectGroup_projects", filters: []) {
										edges {
											node {
												id
												projectColor
												companyProjectId
												name
												status
												billable
												budgetType
												projectGroupId
												sprintTimeBox
												estimationUnit ##for userpilot and storypoints performance##
											}
										}
									}
								}
							}
						}
					}
				`,
			})
		)
	)
);
